import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { RefreshCw, Mail, CheckCircle, AlertCircle, LogOut, Trash2 } from 'lucide-react';
import axios from 'axios';

export default function AdminPanel() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openingSlotsLoading, setOpeningSlotsLoading] = useState(false);
  const [message, setMessage] = useState(null);

  // Check if user is logged in and is admin
  useEffect(() => {
    const userData = localStorage.getItem('adminUser');
    if (!userData) {
      // No admin user - redirect to admin login
      navigate('/admin-login');
      return;
    }

    try {
      const parsedUser = JSON.parse(userData);
      if (parsedUser.admin !== 1) {
        // Not an admin - redirect to login
        navigate('/admin-login');
        return;
      }
      setUser(parsedUser);
      setIsAdmin(true);
    } catch (err) {
      console.error('Error parsing user data:', err);
      navigate('/admin-login');
    }
  }, [navigate]);

  useEffect(() => {
    if (isAdmin) {
      fetchPendingOrders();
    }
  }, [isAdmin]);

  const fetchPendingOrders = async () => {
    try {
      setLoading(true);
      const res = await axios.get('http://localhost:4500/api/admin/pending-orders');
      setPendingOrders(res.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setMessage({ type: 'error', text: 'Fehler beim Laden der Bestellungen' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenSlots = async () => {
    if (!window.confirm(`Wirklich alle ${pendingOrders.length} Kunden benachrichtigen und Slots öffnen?`)) {
      return;
    }

    try {
      setOpeningSlotsLoading(true);
      const token = localStorage.getItem('adminToken');
      const res = await axios.post('http://localhost:4500/api/admin/open-slots', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({
        type: 'success',
        text: `✅ ${res.data.count} Kunden benachrichtigt! Slots sind jetzt verfügbar.`
      });
      await fetchPendingOrders();
    } catch (error) {
      console.error('Error opening slots:', error);
      setMessage({ type: 'error', text: 'Fehler beim Öffnen der Slots' });
    } finally {
      setOpeningSlotsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    navigate('/admin-login');
  };

  const handleDeleteOrder = async (orderId, customerName) => {
    if (!window.confirm(`Bestellung von ${customerName} wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`)) {
      return;
    }

    try {
      const response = await axios.delete(`http://localhost:4500/api/admin/pending-orders/${orderId}`);
      
      if (response.data.success || response.data.deleted) {
        setMessage({
          type: 'success',
          text: `✅ Bestellung von ${customerName} gelöscht!`
        });
        await fetchPendingOrders();
      }
    } catch (error) {
      console.error('Delete error details:', error.response?.data || error.message);
      const errorMsg = error.response?.data?.error || 'Fehler beim Löschen der Bestellung';
      setMessage({ type: 'error', text: `❌ ${errorMsg}` });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white p-4 flex items-center justify-center">
        <div className="animate-spin">
          <RefreshCw size={32} className="text-purple-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white p-4">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 flex justify-between items-start"
        >
          <div>
            <h1 className="text-4xl font-bold mb-2">👑 Admin Panel</h1>
            <p className="text-slate-400">Verwalte ausstehende Bestellungen und öffne Slots</p>
            {user && <p className="text-xs text-purple-300 mt-2">👤 {user.email}</p>}
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 bg-red-900/30 hover:bg-red-900/50 border border-red-500/50 text-red-200 px-4 py-2 rounded-lg transition-all"
          >
            <LogOut size={18} />
            Logout
          </button>
        </motion.div>

        {/* Message */}
        {message && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mb-6 p-4 rounded-lg border-l-4 flex items-center gap-3 ${
              message.type === 'success'
                ? 'bg-green-900/30 border-green-500 text-green-200'
                : 'bg-red-900/30 border-red-500 text-red-200'
            }`}
          >
            {message.type === 'success' ? (
              <CheckCircle size={20} />
            ) : (
              <AlertCircle size={20} />
            )}
            {message.text}
          </motion.div>
        )}

        {/* Summary Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-purple-900 to-slate-900 border-2 border-purple-500/50 rounded-lg p-6 mb-8"
        >
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <div className="text-slate-400 text-sm mb-1">Ausstehende Bestellungen</div>
              <div className="text-4xl font-bold">{pendingOrders.length}</div>
            </div>
            <div>
              <div className="text-slate-400 text-sm mb-1">Gesamtbetrag</div>
              <div className="text-4xl font-bold">
                €{pendingOrders.reduce((sum, o) => sum + (o.amount * 1.19), 0).toFixed(2)}
              </div>
            </div>
            <div className="flex flex-col justify-between">
              <div className="text-slate-400 text-sm mb-1">Aktion</div>
              <button
                onClick={handleOpenSlots}
                disabled={pendingOrders.length === 0 || openingSlotsLoading}
                className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 text-white font-bold py-2 px-4 rounded-lg transition-all flex items-center justify-center gap-2"
              >
                {openingSlotsLoading ? (
                  <>
                    <RefreshCw size={16} className="animate-spin" />
                    Wird versendet...
                  </>
                ) : (
                  <>
                    <Mail size={16} />
                    Slots öffnen & E-Mails
                  </>
                )}
              </button>
            </div>
          </div>
        </motion.div>

        {/* Orders Table */}
        {pendingOrders.length > 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-slate-800/50 border border-purple-500/30 rounded-lg overflow-hidden"
          >
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-purple-900/50 border-b border-purple-500/30">
                  <tr>
                    <th className="p-4 text-left font-bold">Name</th>
                    <th className="p-4 text-left font-bold">Email</th>
                    <th className="p-4 text-left font-bold">Paket</th>
                    <th className="p-4 text-center font-bold">Betrag</th>
                    <th className="p-4 text-left font-bold">Gutschein</th>
                    <th className="p-4 text-left font-bold">Zahlungsart</th>
                    <th className="p-4 text-left font-bold">Adresse</th>
                    <th className="p-4 text-left font-bold">Datum</th>
                    <th className="p-4 text-center font-bold">Aktion</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {pendingOrders.map((order, i) => (
                    <tr key={i} className="hover:bg-slate-700/20 transition">
                      <td className="p-4 font-bold text-purple-300">
                        {order.firstName} {order.lastName}
                      </td>
                      <td className="p-4 text-slate-300">{order.email}</td>
                      <td className="p-4 text-slate-300 capitalize">{order.packageId}</td>
                      <td className="p-4 text-center text-slate-300">
                        €{(order.amount * 1.19).toFixed(2)}
                      </td>
                      <td className="p-4">
                        <code className="bg-slate-700/50 px-2 py-1 rounded text-green-400 text-xs font-bold">
                          {order.voucherCode}
                        </code>
                      </td>
                      <td className="p-4 text-slate-300 capitalize">
                        {order.paymentMethod === 'stripe' ? '💳' : '🔵'} {order.paymentMethod}
                      </td>
                      <td className="p-4 text-slate-400 text-xs">
                        {order.address}, {order.postalCode}
                      </td>
                      <td className="p-4 text-slate-400 text-xs">
                        {new Date(order.createdAt).toLocaleDateString('de-DE')}
                      </td>
                      <td className="p-4 text-center">
                        <button
                          onClick={() => handleDeleteOrder(order.id, `${order.firstName} ${order.lastName}`)}
                          className="bg-red-900/30 hover:bg-red-900/60 text-red-300 hover:text-red-100 p-2 rounded transition-all"
                          title="Diese Bestellung löschen"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-slate-800/50 border-2 border-dashed border-purple-500/30 rounded-lg p-12 text-center"
          >
            <CheckCircle size={48} className="mx-auto mb-4 text-green-400 opacity-50" />
            <h3 className="text-xl font-bold mb-2">Keine ausstehenden Bestellungen!</h3>
            <p className="text-slate-400">Alle Gutscheine wurden bereits eingelöst.</p>
          </motion.div>
        )}
      </div>
    </div>
  );
}
